import { useMemo, useState } from "react";
import { Loader2, Search } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useProjects } from "@/domains/documents/hooks/useProjects";
import { ProjectOptionRow } from "@/domains/projects/components/ProjectOptionRow";
import {
  projectMeta,
  useProjectDocCounts,
} from "@/domains/projects/components/projectSelectorMeta";
import type { BuildingProject } from "@/types/projects";
import { ProjectStatusFilter } from "@/domains/projects/status/ProjectStatusFilter";
import { useProjectStatusFilter } from "@/domains/projects/status/useProjectStatusFilter";
import { orderActiveFirst } from "@/domains/projects/status/projectStatus";

interface WorkInProjectDialogProps {
  isOpen: boolean;
  onClose: () => void;
  buildingId: string;
  /** The currently scoped project id, highlighted in the list. */
  selectedProjectId?: string | null;
  onSelect: (project: BuildingProject) => void;
}

/**
 * WHA-3829 "Work in a project": modal project picker for the sidebar chat.
 * Selecting a project scopes the whole conversation (system prompt + all
 * document/PM tools) to it. Reuses the shared ProjectOptionRow searchable-row
 * presentation without a create-project affordance — this is a pick-only
 * surface.
 */
export function WorkInProjectDialog({
  isOpen,
  onClose,
  buildingId,
  selectedProjectId = null,
  onSelect,
}: WorkInProjectDialogProps) {
  const { t, language } = useLanguage();
  const { projects, isLoading } = useProjects(buildingId);
  const docCounts = useProjectDocCounts(buildingId);
  const [query, setQuery] = useState("");
  const locale = language === "et" ? "et-EE" : "en-GB";

  const statusFilter = useProjectStatusFilter(projects, selectedProjectId);

  const visibleProjects = useMemo(() => {
    // Most recently updated first — within each status, active above inactive.
    const byLastUpdated = orderActiveFirst(
      [...statusFilter.visible].sort(
        (a, b) =>
          new Date(b.modified_at || b.created_at).getTime() -
          new Date(a.modified_at || a.created_at).getTime()
      )
    );
    const q = query.trim().toLowerCase();
    if (!q) return byLastUpdated;
    return byLastUpdated.filter((p) => p.name.toLowerCase().includes(q));
  }, [statusFilter.visible, query]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md gap-0 overflow-hidden p-0">
        <DialogHeader className="border-b px-4 py-4">
          <DialogTitle>{t.documents.chatSidebar.projectScope.pickerTitle}</DialogTitle>
        </DialogHeader>
        {statusFilter.showFilter && (
          <ProjectStatusFilter
            value={statusFilter.filter}
            onValueChange={statusFilter.setFilter}
            counts={statusFilter.counts}
            className="px-2"
          />
        )}
        <div className="flex items-center gap-2 border-b px-4 py-2.5">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t.projects.selector.searchPlaceholder}
            className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            autoFocus
          />
        </div>
        <ScrollArea className="max-h-80">
          <div className="divide-y">
            {isLoading ? (
              <div className="flex items-center justify-center px-4 py-6">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : visibleProjects.length === 0 ? (
              <p className="px-4 py-3 text-sm text-muted-foreground">
                {t.projects.selector.noProjects}
              </p>
            ) : (
              visibleProjects.map((project) => (
                <ProjectOptionRow
                  key={project.id}
                  project={project}
                  meta={projectMeta(project, docCounts?.get(project.id), t, locale)}
                  isSelected={selectedProjectId === project.id}
                  onSelect={() => {
                    onSelect(project);
                    onClose();
                  }}
                />
              ))
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
